import * as THREE from 'three';

export type BrushFalloff = 'none' | 'linear' | 'smooth' | 'gauss';

export class BrushVisualizer {
  readonly group = new THREE.Group();
  private outer: THREE.Line;
  private inner: THREE.Line;
  private epsilon = 1e-3; // extra offset in meters

  private _radius = 0.5;
  private _innerRadius = 0.25;

  constructor(colorOuter = 0x6ee7ff, colorInner = 0xffffff, segments = 64) {
    this.group.renderOrder = 9999;

    const mkRing = (r: number, color: THREE.ColorRepresentation) => {
      const geo = new THREE.BufferGeometry();
      const pos: number[] = [];
      for (let i = 0; i < segments; i++) {
        const t = (i / segments) * Math.PI * 2;
        pos.push(Math.cos(t) * r, Math.sin(t) * r, 0);
      }
      // close loop
      pos.push(pos[0], pos[1], 0);
      geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      const mat = new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
        depthTest: true,
      });
      return new THREE.Line(geo, mat);
    };

    this.outer = mkRing(this._radius, colorOuter);
    this.inner = mkRing(this._innerRadius, colorInner);

    this.group.add(this.outer);
    this.group.add(this.inner);
    this.group.visible = false;
  }

  setVisible(v: boolean) {
    this.group.visible = v;
  }

  setRadius(r: number) {
    this._radius = Math.max(1e-6, r);
    this.updateRing(this.outer, this._radius);
  }

  /** Sets falloff visualization (just inner ring cue). For gauss we show sigma; otherwise a half-radius. */
  setFalloff(kind: BrushFalloff, sigma?: number) {
    const rInner =
      kind === 'gauss' && sigma && sigma > 0 ? Math.min(this._radius, sigma) : this._radius * 0.5;
    this._innerRadius = Math.max(1e-6, rInner);
    this.updateRing(this.inner, this._innerRadius);
  }

  /** Place the brush slightly above the surface to avoid z-fighting. */
  setPose(point: THREE.Vector3, normal: THREE.Vector3) {
    const n = normal.clone().normalize();
    const up = new THREE.Vector3(0, 0, 1);
    // if normal ~ up, avoid NaN quaternion
    const q =
      Math.abs(n.dot(up)) > 0.999
        ? new THREE.Quaternion()
        : new THREE.Quaternion().setFromUnitVectors(up, n);

    this.group.position.copy(point).addScaledVector(n, Math.max(this.epsilon, this._radius * 0.002));
    this.group.quaternion.copy(q);
  }

  private updateRing(line: THREE.Line, radius: number) {
    const attr = line.geometry.getAttribute('position') as THREE.BufferAttribute;
    const arr = attr.array as Float32Array;
    for (let i = 0; i < arr.length; i += 3) {
      const t = (i / 3 / ((arr.length / 3) - 1)) * Math.PI * 2;
      arr[i + 0] = Math.cos(t) * radius;
      arr[i + 1] = Math.sin(t) * radius;
      // z stays 0 in local brush plane
    }
    attr.needsUpdate = true;
    line.geometry.computeBoundingSphere();
  }

  dispose() {
    [this.outer, this.inner].forEach((l) => {
      l.geometry.dispose();
      (l.material as THREE.Material).dispose();
    });
  }
}